import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import ConfirmDialog from 'components/dialogs/ConfirmDialog';
import useModal from 'hooks/useModal';
import { UserData, UserModel } from 'models/UserModel';
import UserDialog from 'modules/users/dialogs/UserDialog';
import YardSelectDialog from 'modules/users/dialogs/YardSelectDialog';
import VesselSelectDialog from 'modules/companies/dialogs/VesselSelectDialog';
import userListFamily from 'recoil/atoms/userListFamily';
import Api from 'utils/api';
import { removeItemAtIndex, replaceItemAtIndex } from 'utils/common';


const useUserModals = (companyId: number) => {
    const {t} = useTranslation();
    const setUsers = useSetRecoilState(userListFamily(companyId));
    const {showModal, hideModal} = useModal();
    const {enqueueSnackbar} = useSnackbar();

    const createUser = (user: UserData, companyId: number) => Api.users.create(user, companyId)
        .then(res => setUsers(old => [res.data.data, ...old]));

    const updateUser = (user: UserModel) => Api.users.update(user)
        .then(res => setUsers(old => replaceItemAtIndex(old, old.findIndex(i => i.id === user.id), res.data)));

    const setYards = (userId: string, yards: number[]) => Api.users.setYards(userId, yards)
        .then(res => setUsers(old => replaceItemAtIndex(old, old.findIndex(i => i.id === userId), res.data)));

    const openAddUserModal = (company: UserModel['company']) => showModal(UserDialog,
        {company},
        {
            title: t('add_user_to', {value: company.name})
        })
        .then(user => createUser(user, company.id))
        .then(hideModal);

    const openEditUserModal = (user: UserModel) => showModal(UserDialog,
        {
            company: user.company,
            user,
        },
        {
            title: t('edit_user')
        })
        .then(updateUser)
        .then(hideModal);

    const confirmDeleteUser = (user: UserModel) => showModal(ConfirmDialog,
        {
            description: `${user.lastName} ${user.firstName}`
        },
        {
            title: t('confirm_delete_user'),
            fullScreen: false,
        })
        .then(() => Api.users.delete(user.id))
        .then(() => setUsers(old => removeItemAtIndex(old, old.findIndex(i => i.id === user.id))))
        .finally(hideModal);

    const confirmSendVerification = (user: UserModel) => showModal(ConfirmDialog,
        {
            description: `${user.lastName} ${user.firstName}`
        },
        {
            title: t('confirm_send_verification_link'),
            fullScreen: false,
        })
        .then(() => Api.users.sendVerification(user.id))
        .then(() => enqueueSnackbar(t('link_sent'), {variant: 'success'}))
        .finally(hideModal);

    const openYardSelectModal = (user: UserModel) => showModal(YardSelectDialog,
        {saved: user.workAtYards!.map(yard => yard.id)},
        {
            title: '',
            maxWidth: 'xs',
        })
        .then(yards => setYards(user.id, yards))
        .then(hideModal);

    const openVesselSelectModal = (user: UserModel) => showModal(VesselSelectDialog,
        {saved: user.captainAtVessels?.map(vessel => vessel.id) || []},
        {
            title: '',
            maxWidth: 'xs',
        })
        .then(vessels => Api.users.setVessels(user.id, vessels))
        .then(res => {
            setUsers(old => replaceItemAtIndex(old, old.findIndex(i => i.id === user.id), res.data));
        })
        .then(hideModal);

    return {
        openAddUserModal,
        openEditUserModal,
        confirmDeleteUser,
        confirmSendVerification,
        openYardSelectModal,
        openVesselSelectModal,
    };
};

export default useUserModals;
